import {
  Component,
  OnInit,
  Inject,
  Optional,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { TagService } from '../../../core/services/tag.service';
import { Tag } from '../../../core/types/tag';
import { SearchDirection, SearchOptions } from '@app/core/types/search-options';
import { finalize, Subject, takeUntil } from 'rxjs';
import { Page } from '@app/core/types/page';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from '@app/core/services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { DialogProviderService } from '@app/core/services/dialog-provider.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@Component({
    selector: 'itfg-tag-settings-card',
    templateUrl: './tag-settings-card.component.html',
    styleUrls: ['./tag-settings-card.component.scss'],
    animations: [
        trigger('cardState', [
            transition(':enter', [
                style({
                    transformOrigin: 'top',
                    transform: 'scaleY(0)',
                    opacity: 0,
                }),
                animate(150, style({
                    transformOrigin: 'top',
                    transform: 'scaleY(1)',
                    opacity: 1,
                })),
            ]),
            transition(':leave', [
                style({
                    transformOrigin: 'top',
                    transform: 'scaleY(1)',
                    opacity: 1,
                }),
                animate(150, style({
                    transformOrigin: 'top',
                    transform: 'scaleY(0)',
                    opacity: 0,
                })),
            ]),
        ]),
    ],
    standalone: false
})
export class TagSettingsCardComponent implements OnInit, OnDestroy {
  public tagsList: Tag[] = [];
  public initialTagsList: Tag[] = [];
  public tagSearchOptions: SearchOptions;
  public disableDrag: boolean = false;
  public isLoadingTags: boolean = false;
  private isDragging: boolean = false;

  public _unsubscribe: Subject<void> = new Subject<void>();
  @ViewChild('tagsList') tagsListElement: ElementRef;

  constructor(
    @Optional() public dialogRef: MatDialogRef<TagSettingsCardComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private tagService: TagService,
    private notifications: NotificationService,
    private translate: TranslateService,
    private dialogService: DialogProviderService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.loadTags();
  }

  loadTags() {
    this.isLoadingTags = true;

    this.tagService.getTagList$(new SearchOptions({ pageSize: 6000, column: 'id', direction: SearchDirection.DESCENDING }).getDTO())
      .pipe(
        takeUntil(this._unsubscribe),
        finalize(() => {
          this.isLoadingTags = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (tagList: Page<Tag>) => {
          const tags = tagList.content || [];
          this.initialTagsList = [...tags];

          this.tagsList = this.tagService.applyTagsPreferences(tags);
          this.cdr.detectChanges();
        },
        error: (error: HttpErrorResponse) => {
          this.notifications.showLocalizedErrorMessage({
            notificationText: 'tags.fetchTagsFailMessage'
          });
          console.error('Error loading tags:', error);

          this.tagsList = [];
        },
      });
  }

  onDrop(event: CdkDragDrop<string[]>) {
    // Do nothing if element is dropped in the same position as before
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.tagsList, event.previousIndex, event.currentIndex);
    this.saveTagsPreferences();
    this.isDragging = false;
    this.disableDrag = false;

    this.notifications.showLocalizedSuccessMessage({
      notificationText: 'tags.reorderTagsSuccessMessage'
    });
  }

  resetTags() {
    const dialogConfig = {
      message: this.translate.instant('tags.resetTagSettingsConfirmationMessage'),
      disableClose: false,
      title: this.translate.instant('tags.resetTagSettings'),
      cancelButton: this.translate.instant('global.cancel'),
      acceptButton: this.translate.instant('global.confirm'),
    };

    this.dialogService
      .openConfirm(dialogConfig)
      .afterClosed()
      .subscribe((accept: boolean) => {
        if (accept) {
          this.tagsList = [...this.initialTagsList].map((tag: Tag) => ({
            ...tag,
            visible: true
          }));

          this.tagService.resetTagPreferences();
          this.tagService.saveTagPreferences(this.tagsList);

          this.cdr.detectChanges();

          this.notifications.showLocalizedSuccessMessage({
            notificationText: 'tags.resetTagSettingsSuccessMessage'
          });
        }
      });
  }

  onTagVisibilityChange(tagId: number, event: MatSlideToggleChange) {
    const tag = this.tagsList.find((t: Tag) => t.id === tagId);
    if (tag) {
      tag.visible = event.checked;
      this.tagService.updateTagVisibility(tagId, event.checked);
    }

    this.notifications.showLocalizedSuccessMessage({
      notificationText: event.checked ? 'tags.tagVisibleSuccessMessage' : 'tags.tagHiddenSuccessMessage'
    });
  }

  saveTagsPreferences() {
    this.tagService.saveTagPreferences(this.tagsList);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  onMouseDown() {
    this.disableDrag = false;
    this.isDragging = true;
  }

  onMouseUp() {
    if (!this.isDragging) {
      this.disableDrag = false;
    }
    this.isDragging = false;
  }

  scrollToTop() {
    if (this.tagsListElement && this.tagsListElement.nativeElement) {
      this.tagsListElement.nativeElement.scrollTop = 0;
    }
  }

  scrollToBottom() {
    if (this.tagsListElement && this.tagsListElement.nativeElement) {
      this.tagsListElement.nativeElement.scrollTop = this.tagsListElement.nativeElement.scrollHeight;
    }
  }

  ngOnDestroy() {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}